/**
 * Calendar arithmetic that never goes through local-time `Date` getters or
 * `setMonth` (QL-692). `new Date("YYYY-MM-DD")` is UTC midnight but
 * `getMonth()` reads the host zone (one month early west of UTC), and
 * `setMonth()` overflows on the 29th–31st (31 Jan + 1 month = 3 Mar). These
 * helpers work on strings and UTC fields only, so they give the same answer
 * on every host and in every browser.
 */

/**
 * "YYYY-MM" → its year and 0-indexed month (to match `Date` and
 * `zonedDateParts`), or null when the value is not exactly that shape.
 */
export function parseYearMonth(
  value: string | null | undefined,
): { year: number; monthIndex: number } | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value ?? "");
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return { year: Number(match[1]), monthIndex: month - 1 };
}

/**
 * `date` moved by `months` calendar months (negative = back) in UTC. The day
 * is clamped to the target month's length (31 Jan + 1 → 28/29 Feb) and the
 * UTC time of day is kept. Never mutates `date`.
 */
export function addMonthsUtc(date: Date, months: number): Date {
  const year = date.getUTCFullYear();
  const monthIndex = date.getUTCMonth() + months;
  const daysInTarget = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const result = new Date(date.getTime());
  result.setUTCFullYear(year, monthIndex, Math.min(date.getUTCDate(), daysInTarget));
  return result;
}

/**
 * The last day of the month before the one `dateIso` ("YYYY-MM-DD", extra
 * characters after the day are ignored) falls in, as "YYYY-MM-DD" — e.g.
 * "2026-03-31" → "2026-02-28". Null when the input is not a calendar date.
 */
export function lastDayOfPreviousMonth(dateIso: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateIso);
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return new Date(Date.UTC(Number(match[1]), month - 1, 0)).toISOString().slice(0, 10);
}

/**
 * The synthetic start point a portfolio forecast chart draws one month before
 * its first entry, shared by the web widget and the PDF block so the two stay
 * in step. Forecast dates are month-ends, so the start is the previous
 * month-end, read from the string (QL-692: `setMonth` overflowed on 31sts and
 * mixed UTC/local). A first date that is not "YYYY-MM-DD" is returned as is.
 */
export function forecastStartDate(firstEntryDate: string): string {
  return lastDayOfPreviousMonth(firstEntryDate) ?? firstEntryDate;
}
